import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { AuthorComposerViewStore } from "../../store";
import { AuthorComposerView } from "./AuthorComposerView";
import { AuthorComposerUpdateStore } from "../../store";
import { AuthorComposerUpdate } from "../update/AuthorComposerUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type AuthorComposerDrawerContentProps = {
  entityId: string;
  mode?: "view" | "update";
};

export const AuthorComposerDrawerContent: React.FC<AuthorComposerDrawerContentProps> =
  observer(({ entityId, mode = "view" }) => {
    const rootStore = useRootStore();
    const viewInitializedRef = useRef<string | null>(null);
    const updateInitializedRef = useRef<string | null>(null);

    const viewStore = useMemo(() => {
      const s = new AuthorComposerViewStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "view" &&
        entityId &&
        viewInitializedRef.current !== entityId
      ) {
        viewInitializedRef.current = entityId;
        viewStore.initialize({ id: entityId });
      }
    }, [entityId, mode, viewStore]);

    const updateStore = useMemo(() => {
      const s = new AuthorComposerUpdateStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "update" &&
        entityId &&
        updateInitializedRef.current !== entityId
      ) {
        updateInitializedRef.current = entityId;
        updateStore.initialize({ id: entityId });
      }
    }, [entityId, mode, updateStore]);

    if (mode === "update") {
      if (
        updateStore.status === "processing" ||
        updateStore.status === "uninitialized"
      ) {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        );
      }
      return <AuthorComposerUpdate authorComposerUpdateStore={updateStore} />;
    }

    if (
      viewStore.status === "processing" ||
      viewStore.status === "uninitialized"
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return <AuthorComposerView authorComposerViewStore={viewStore} />;
  });
